/**
 * Générateur — Automatisme : calculer la valeur d'une expression algébrique
 * avec des puissances ou non.
 * Attendu ID : 1228 | Niveau : 3ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, ligne 796).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * Substituer un nombre à la lettre et calculer : gen neuf sur les
 * évaluateurs de gen_1111 (4e, « tester une égalité », substitution ax + b)
 * et les familles de gen_931 (5e, x², 3x², (3x)²). Le NEUF de 3e : le carré
 * (3x² contre (3x)²), le cube (rare), et x NÉGATIF (une ligne sur quatre,
 * écrit entre parenthèses dans les explications ; jamais « −2² » dans un
 * énoncé). Formes = bornes : ax + b, b + ax, b − ax, ax², ax² + b, (ax)²
 * (une sur quatre des carrés), ax³ (x ≤ 3, rare) ; exclus : trois termes,
 * (x + a)(x + b), x en fraction. Choix mode auto, À ARBITRER : part de x
 * négatif (un quart), ax³ admis rare, b − ax admis.
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 5NE3
 * (a + bx, a + bx², a + bx³, (x + a) × b ; x 0..5 ou −5..−1), 5NE10 ; retenu
 * a 2..5, b 1..9, x 0..6 ou −5..−1.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes KaTeX
 *     « Pour x = 4 : 3x² + 1 = ⬚ », un carré au moins, un x négatif une fois
 *     sur quatre, valeurs distinctes.
 *   Carte 2 « Combien vaut… ? » (valeur_qcm_unique) : « Pour x = 4 ; 3x²
 *     vaut… » (point-virgule entre les deux écritures, retour prof), QUATRE choix ; contenu.sousType = carre / negatif / simple ;
 *     carré [48 ; 144 ; 24 ; 12], négatif [−1 ; 1 ; 11 ; −11], simple
 *     (l'item DEPP 2de 2025 q.4 « 3x + 2 pour x = 5 » : [17 ; 25 ; 37 ; 10]).
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : quatre lignes « Pour x = −2,
 *     x² = 4 » vérifiées en valeur.
 *
 * INVARIANTS : chaque valeur re-calculée par un second chemin (throw) ;
 * distracteurs distincts de la clé et entre eux ; x = 0 et x = 1 rares
 * (ils écrasent les familles).
 * SACRIFIÉS (fiches « Avec un carré » 3 × 4² = 48 et « x négatif » 2 × (−3)
 * + 5 = −1) : 3x + 2 pour tout x (importé de 1111, À ARBITRER), (3x² ; 4),
 * ((3x)² ; 4), (2x + 5 ; −3) ; auto-testés par gen1228.self.test.js.
 *
 * ── Erreurs (familles nommées ; DEPP 2de 2025 q.4 « 3x + 2 pour x = 5 »,
 * réussite 88,9 %) ────────────────────────────────────────────────────────
 *   gauche_a_droite     : (3 + 2) × 5 = 25 (sans carré), PRINCIPALE ;
 *   produit_au_carre    : 3x² → (3 × 4)² = 144 (avec carré), PRINCIPALE ;
 *   parentheses_ignorees : (3x)² → 3 × 4² = 48 ;
 *   double_pour_carre   : x² → 2x (3 × 2 × 4 = 24) ;
 *   carre_oublie        : 3x² → 3 × 4 = 12 ;
 *   concatenation       : 3x → 35 (q.4, 1,3 %) ;
 *   lecture_additive    : 3x → 3 + x (q.4, 3,9 %) ;
 *   signe_perdu         : x = −3 calculé avec 3 ;
 *   moins_partout       : le résultat entier changé de signe ;
 *   carre_negatif       : (−2)² → −4.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1228;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Combien vaut… ?';
const L_VF = 'Vrai ou faux';

const LETTRES = ['x', 'x', 'x', 'n', 't', 'y'];

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Les exemples des fiches (et 3x + 2 pour tout x, importé de 1111). */
export const SACRIFIES = [
    { forme: 'ax_b', a: 3, b: 2 }, { forme: 'b_ax', a: 3, b: 2 }, // pour tout x
    { forme: 'ax2', a: 3, x: 4 },
    { forme: 'ax_carre', a: 3, x: 4 },
    { forme: 'ax_b', a: 2, b: 5, x: -3 }, { forme: 'b_ax', a: 2, b: 5, x: -3 },
];
const estSacrifie = (e, x) => SACRIFIES.some((s) => s.forme === e.forme && s.a === e.a && (s.b === undefined || s.b === e.b) && (s.x === undefined || s.x === x));

/** La valeur exacte, par la forme (chemin 1) puis par une écriture développée (chemin 2, assert). */
const valeur = (e, x) => {
    const { forme, a, b } = e;
    const v = { ax_b: a * x + b, b_ax: b + a * x, b_moins_ax: b - a * x, ax2: a * x * x, ax2_b: a * x * x + b, ax_carre: (a * x) * (a * x), ax3: a * x * x * x }[forme];
    const controle = { ax_b: b + x * a, b_ax: x * a + b, b_moins_ax: -(a * x) + b, ax2: a * (x ** 2), ax2_b: b + a * (x ** 2), ax_carre: (a ** 2) * (x ** 2), ax3: a * (x ** 3) }[forme];
    if (v !== controle || !Number.isInteger(v)) throw new Error(`gen_1228 : ${forme} ${a} ${b} ${x}`);
    return v;
};

/** Une expression { forme, a, b } et une valeur x : `carre` impose une forme au carré ; `negatif` impose x < 0. */
const tirer = ({ carre = null, negatif = null, exclure = [] } = {}) => {
    for (let essai = 0; essai < 300; essai++) {
        const formes = carre === true ? ['ax2', 'ax2', 'ax2_b', 'ax_carre'] : carre === false ? ['ax_b', 'ax_b', 'b_ax', 'b_moins_ax'] : ['ax_b', 'b_ax', 'b_moins_ax', 'ax2', 'ax2_b', 'ax_carre', 'ax3'];
        const forme = randomChoice(formes);
        if (forme === 'ax3' && randInt(1, 3) !== 1) continue; // rare
        const a = randInt(2, 5);
        const b = randInt(1, 9);
        const neg = negatif ?? randInt(1, 4) === 1;
        let x = neg ? -randInt(1, 5) : randInt(1, 6);
        if (forme === 'ax3') x = neg ? -randInt(1, 3) : randInt(2, 3);
        if (!neg && randInt(1, 6) === 1) x = randomChoice([0, 1]); // rares
        if (forme === 'b_moins_ax' && b - a * x < -20) continue;
        const e = { forme, a, b };
        if (estSacrifie(e, x) || exclure.includes(`${forme}${a}${b}${x}`)) continue;
        const v = valeur(e, x);
        if (Math.abs(v) > 200) continue;
        return { e, x, v };
    }
    return { e: { forme: 'ax2_b', a: 2, b: 3 }, x: 3, v: 21 };
};

// ─── Écritures (KaTeX) ────────────────────────────────────────────────────

const expressionTex = ({ forme, a, b }, L) => ({
    ax_b: `${a}${L} + ${b}`,
    b_ax: `${b} + ${a}${L}`,
    b_moins_ax: `${b} - ${a}${L}`,
    ax2: `${a}${L}^{2}`,
    ax2_b: `${a}${L}^{2} + ${b}`,
    ax_carre: `(${a}${L})^{2}`,
    ax3: `${a}${L}^{3}`,
}[forme]);
const sousTypeDe = (e, x) => (x < 0 ? 'negatif' : ['ax2', 'ax2_b', 'ax_carre', 'ax3'].includes(e.forme) ? 'carre' : 'simple');

const EXPLICATIONS = {
    gauche_a_droite: 'La multiplication passe avant l\'addition : 3x + 2 pour x = 5, c\'est 3 × 5 + 2 = 17, pas (3 + 2) × 5.',
    produit_au_carre: '3x², c\'est 3 × x² : le carré porte sur x seulement. Pour x = 4 : 3 × 16 = 48, pas (3 × 4)².',
    parentheses_ignorees: '(3x)² : les parenthèses mettent tout au carré, (3 × 4)² = 12² = 144.',
    double_pour_carre: 'x², c\'est x × x, pas 2 × x : 4² = 16.',
    carre_oublie: 'Le carré compte : 3x² pour x = 4, c\'est 3 × 16, pas 3 × 4.',
    concatenation: '3x, c\'est 3 × x : pour x = 5, 3 × 5 = 15, pas 35.',
    lecture_additive: '3x, c\'est 3 × x, pas 3 + x.',
    signe_perdu: 'Je remplace x par −3 avec son signe : 2 × (−3) + 5 = −6 + 5 = −1.',
    moins_partout: 'Seul x est négatif : 2 × (−3) + 5 = −1, le 5 reste positif.',
    carre_negatif: '(−2)², c\'est (−2) × (−2) = 4 : le carré d\'un négatif est positif.',
    regle: 'Je remplace la lettre par sa valeur, entre parenthèses si elle est négative, puis je calcule dans l\'ordre : puissances, multiplications, additions.',
};

// ─── Distracteurs ─────────────────────────────────────────────────────────

/** Les candidats d'un item, entiers, jamais la clé, jamais deux fois la même valeur. */
const candidats = ({ e, x, v }) => {
    const { forme, a, b } = e;
    const c = [];
    const pousser = (code, val) => { if (Number.isInteger(val) && val !== v && !c.some((y) => y.val === val)) c.push({ code, val }); };
    if (x < 0) {
        const auPositif = valeur(e, -x);
        pousser('signe_perdu', auPositif);
        pousser('moins_partout', -auPositif);
        if (['ax2', 'ax2_b', 'ax_carre'].includes(forme)) pousser('carre_negatif', forme === 'ax2' ? -v : forme === 'ax2_b' ? -a * x * x + b : -v);
        if (['ax_b', 'b_ax'].includes(forme)) pousser('lecture_additive', a + x + b);
        if (forme === 'b_moins_ax') pousser('gauche_a_droite', (b - a) * x);
    }
    if (['ax2', 'ax2_b'].includes(forme)) {
        pousser('produit_au_carre', (a * x) * (a * x) + (forme === 'ax2_b' ? b : 0));
        pousser('double_pour_carre', a * 2 * x + (forme === 'ax2_b' ? b : 0));
        pousser('carre_oublie', a * x + (forme === 'ax2_b' ? b : 0));
    }
    if (forme === 'ax_carre') {
        pousser('parentheses_ignorees', a * x * x);
        pousser('double_pour_carre', 2 * a * x);
        pousser('carre_oublie', a * x);
    }
    if (forme === 'ax3') {
        pousser('produit_au_carre', (a * x) ** 3);
        pousser('double_pour_carre', 3 * a * x);
        pousser('carre_oublie', a * x);
    }
    if (['ax_b', 'b_ax'].includes(forme)) {
        pousser('gauche_a_droite', (a + b) * x);
        if (x >= 0) pousser('concatenation', Number(`${a}${x}`) + b);
        pousser('lecture_additive', a + x + b);
    }
    if (forme === 'b_moins_ax') {
        pousser('gauche_a_droite', (b - a) * x);
        pousser('moins_partout', -v);
        pousser('lecture_additive', b - a - x);
    }
    return c;
};

/** Trois distracteurs de familles distinctes ; null s'il en manque. */
const distracteurs = (it) => {
    const pris = [];
    for (const y of shuffle(candidats(it))) if (!pris.some((z) => z.code === y.code)) pris.push(y);
    return pris.length >= 3 ? pris.slice(0, 3) : null;
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const ligneFlash = ({ e, x, v }, L) => `$\\text{Pour } ${L} = ${x} \\text{ : } ${expressionTex(e, L)} = {{${v}}}$`;

const genFlash = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        // trois lignes (retour prof 13/09) : un carré, une simple, un x négatif
        const items = [];
        items.push(tirer({ carre: true, negatif: false }));
        items.push(tirer({ carre: false, negatif: false }));
        items.push(tirer({ negatif: true, exclure: items.map((it) => `${it.e.forme}${it.e.a}${it.e.b}${it.x}`) }));
        if (new Set(items.map((it) => it.v)).size < 3 || new Set(items.map((it) => expressionTex(it.e, L))).size < 3) continue;
        const payload = makeTexteTrou(shuffle(items).map((it) => ligneFlash(it, L)).join('\n'), '<p><b>Calcule.</b></p>', { ...opts(L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, produit_au_carre: EXPLICATIONS.produit_au_carre, signe_perdu: EXPLICATIONS.signe_perdu } };
        payload._meta.params = { items, lettre: L };
        return payload;
    }
    const items = [{ e: { forme: 'ax2_b', a: 2, b: 3 }, x: 3, v: 21 }, { e: { forme: 'b_ax', a: 4, b: 1 }, x: 5, v: 21 + 0 }, { e: { forme: 'ax_b', a: 4, b: 7 }, x: -2, v: -1 }];
    items.forEach((it) => { it.v = valeur(it.e, it.x); });
    const payload = makeTexteTrou(items.map((it) => ligneFlash(it, 'x')).join('\n'), '<p><b>Calcule.</b></p>', { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items, lettre: 'x' };
    return payload;
};

// ─── Carte 2 : Combien vaut… ? (QCM) ──────────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 200; essai++) {
        const L = randomChoice(LETTRES);
        const it = tirer();
        if ((it.x === 0 || it.x === 1) && randInt(1, 3) !== 1) continue; // 0 et 1 écrasent les familles
        const faux = distracteurs(it);
        if (!faux) continue;
        const bon = String(it.v);
        const choix = [bon, ...faux.map((f) => String(f.val))];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p><b>Pour ${tex(`${L} = ${it.x}`)} ; ${tex(expressionTex(it.e, L))} vaut…</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, sousType: sousTypeDe(it.e, it.x), explications: EXPLICATIONS };
        payload._meta.params = { ...it, lettre: L, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 2x² + 3 pour x = 3 → 21 ; 39 (produit_au_carre), 15 (double_pour_carre), 9 (carre_oublie).
    const it = { e: { forme: 'ax2_b', a: 2, b: 3 }, x: 3, v: 21 };
    const payload = makeQcm(['21', '39', '15', '9'], '21', `<p><b>Pour ${tex('x = 3')} ; ${tex(expressionTex(it.e, 'x'))} vaut…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, sousType: 'carre', explications: EXPLICATIONS };
    payload._meta.params = { ...it, lettre: 'x', distracteurs: [{ code: 'produit_au_carre' }, { code: 'double_pour_carre' }, { code: 'carre_oublie' }] };
    return payload;
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

const genVf = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const items = [tirer({ carre: true }), tirer({ negatif: true }), tirer(), tirer()];
        if (new Set(items.map((it) => `${expressionTex(it.e, L)}|${it.x}`)).size < 4) continue;
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = items.map((it, i) => {
            if (verites[i]) return { ligne: tex(`\\text{Pour } ${L} = ${it.x} \\text{ : } ${expressionTex(it.e, L)} = ${it.v}`), code: null };
            const cands = candidats(it);
            if (!cands.length) return null;
            const y = randomChoice(cands);
            return { ligne: tex(`\\text{Pour } ${L} = ${it.x} \\text{ : } ${expressionTex(it.e, L)} = ${y.val}`), code: y.code };
        });
        if (lignes.some((l) => !l) || new Set(lignes.map((l) => l.ligne)).size < 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, lettre: L, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : x = −2, x² = 4 → 2x² = 8 V ; x = 3, (2x)² = 18 F ; x = 5, 4x + 1 = 21 V ; x = −2, 4x + 7 = 15 F (signe_perdu).
    const lignes = [tex('\\text{Pour } x = -2 \\text{ : } 2x^{2} = 8'), tex('\\text{Pour } x = 3 \\text{ : } (2x)^{2} = 18'), tex('\\text{Pour } x = 5 \\text{ : } 4x + 1 = 21'), tex('\\text{Pour } x = -2 \\text{ : } 4x + 7 = 15')];
    const payload = makeVraiFaux(lignes, [0, 1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [], lettre: 'x', verites: [true, false, true, false], distracteurs: [{ code: 'parentheses_ignorees' }, { code: 'signe_perdu' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre entiers distincts ; x = 0 et x = 1 rares ; contenu.sousType = carre / negatif / simple',
        'sacrifiés : 3x + 2 pour tout x, (3x² ; 4), ((3x)² ; 4), (2x + 5 ; −3)',
    ],
    valeur_qcm_unique: {
        familles: {
            gauche_a_droite: '(a + b) × x (DEPP 2de 2025 q.4), la principale sans carré',
            produit_au_carre: '(ax)² pour ax² (144 pour 48), la principale avec carré',
            parentheses_ignorees: 'ax² pour (ax)²',
            double_pour_carre: 'x² lu 2x',
            carre_oublie: 'x² lu x',
            concatenation: '3x lu 35 (q.4, 1,3 %)',
            lecture_additive: '3x lu 3 + x (q.4, 3,9 %)',
            signe_perdu: 'x négatif calculé au positif',
            moins_partout: 'le résultat changé de signe',
            carre_negatif: '(−2)² lu −4',
        },
    },
    vf_tableau_vf: 'quatre lignes « Pour x = … : … = … », un carré, un x négatif ; les fausses tirent une famille',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    valeur_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'valeur', label: L_QCM, formatsList: [{ key: 'valeur_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
